import { useState, type ReactNode } from "react";
// Menu and footer are imported directly, not lazily: this page is
// prerendered, and a lazy part inside it would hydrate in a later pass, where
// an update from above makes React replace its HTML (src/lib/lazyComponent.ts).
import MegaMenu from "@/components/MegaMenu";
import Footer from "@/components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import SEOHead from "@/components/SEOHead";
import RelatedResources, { getPageLinks } from "@/components/RelatedResources";
import { useLanguage } from "@/contexts/LanguageContext";
import { isFigmaUrl } from "@/utils/figmaUrlDetection";
import { cn } from "@/lib/utils";
import { HOME_THEME, MONO, SANS } from "@/components/home-a/theme";
import { Reveal, RevealAccent, RevealGroup } from "@/components/home-a/Reveal";
import { CountUp, DrawLine, Parallax, ScrollLine, ScrollProgress } from "@/components/home-a/Motion";
import { Capture, MOBILE_MARKERS, Num } from "@/components/home-a/StorePage";
import { Glyph, Kicker, ScoreBadge, type Level } from "@/components/home-a/Parts";

/**
 * The public site kit.
 *
 * Every marketing page is built from these pieces so it reads as one site with
 * the home page: the green-black ground and logo green (HOME_THEME), Geist,
 * sections numbered like a document, the one primary action in the logo
 * gradient, and a sample report on a demo page wherever a picture explains
 * better than a paragraph.
 */

export type Tr = (el: string, en: string) => string;

export function useTr() {
  const { language } = useLanguage();
  const isEl = language === "el";
  const tr: Tr = (el, en) => (isEl ? el : en);
  return { tr, isEl };
}


/** Page shell: theme, fonts, head tags, menu, scroll progress, related links and footer. */
export function SitePage({
  children,
  related,
  seo,
}: {
  children: ReactNode;
  /** Key into RelatedResources' page map. */
  related?: string;
  /** Only for dynamic pages (blog posts); static routes read src/seo/route-meta.ts. */
  seo?: Parameters<typeof SEOHead>[0];
}) {
  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen overflow-x-clip bg-background text-foreground antialiased" style={{ ...HOME_THEME, fontFamily: SANS }}>
        <SEOHead {...(seo ?? {})} />
        <ScrollProgress />
        <MegaMenu />
        <main>{children}</main>
        {related && <RelatedResources links={getPageLinks(related)} />}
        <Footer />
      </div>
    </LazyMotion>
  );
}

/* ------------------------------------------------------------------ */
/* Calls to action                                                     */
/* ------------------------------------------------------------------ */

/** Straight to the free audit with the URL filled in. */
export function useStartAudit() {
  const navigate = useNavigate();
  return (raw: string) => {
    const value = raw.trim();
    if (!value || isFigmaUrl(value)) {
      navigate("/free-audit");
      return;
    }
    const url = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    navigate(`/free-audit?url=${encodeURIComponent(url)}`);
  };
}

export function UrlForm({
  id,
  value,
  setValue,
  onSubmit,
  placeholder,
  button,
  label,
  className,
}: {
  id: string;
  value: string;
  setValue: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  button: string;
  label: string;
  className?: string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className={cn("flex flex-col gap-2 sm:h-[60px] sm:flex-row sm:items-center sm:gap-3 sm:rounded-[14px] sm:border sm:border-[hsl(var(--ec-line-strong))] sm:bg-card sm:py-1.5 sm:pl-5 sm:pr-1.5", className)}
    >
      <label htmlFor={id} className="sr-only">{label}</label>
      <div className="flex h-12 flex-1 items-center gap-2 rounded-xl border border-[hsl(var(--ec-line-strong))] bg-card px-4 sm:h-auto sm:border-0 sm:bg-transparent sm:px-0">
        <span aria-hidden className="text-[14px] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>https://</span>
        <input
          id={id}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          inputMode="url"
          className="h-11 min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-[hsl(var(--ec-muted))]"
        />
      </div>
      <button type="submit" className="ec-cta inline-flex h-12 items-center justify-center gap-2 rounded-[10px] px-5 text-[15px] font-semibold">
        {button}
      </button>
    </form>
  );
}

/** The URL form wired to the free audit, with the no-card line under it. */
export function AuditForm({ id, className, placeholder, button, center = false }: { id: string; className?: string; placeholder?: string; button?: string; center?: boolean }) {
  const { tr } = useTr();
  const start = useStartAudit();
  const [value, setValue] = useState("");
  return (
    <div className={className}>
      <UrlForm
        id={id}
        value={value}
        setValue={setValue}
        onSubmit={() => start(value)}
        placeholder={placeholder ?? tr("το-site-σας.gr", "yourstore.com")}
        button={button ?? tr("Δωρεάν ανάλυση", "Analyse free")}
        label={tr("URL σελίδας", "Page URL")}
      />
      <p className={cn("mt-3.5 text-[13px] text-[hsl(var(--ec-muted))]", center && "text-center")}>
        {[tr("Χωρίς κάρτα", "No card"), tr("Αποτέλεσμα σε λιγότερο από 5 λεπτά", "Result in under 5 minutes"), tr("Desktop και mobile", "Desktop and mobile")].join("  ·  ")}
      </p>
    </div>
  );
}

/** A link styled as the primary (green), the second (white) or the quiet action. */
export function ActionLink({ to, children, variant = "primary", className }: { to: string; children: ReactNode; variant?: "primary" | "white" | "ghost"; className?: string }) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex h-12 items-center justify-center gap-2 rounded-[10px] px-5 text-[15px] font-semibold transition-colors",
        variant === "primary"
          ? "ec-cta"
          : variant === "white"
            ? "ec-cta-white"
            : "border border-[hsl(var(--ec-line-strong))] font-medium text-foreground hover:border-[hsl(var(--ec-mint)/0.5)] hover:bg-foreground/[0.03]",
        className,
      )}
    >
      {children}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

/**
 * Page hero. Text on the left and a picture of the product on the right on
 * wide screens; stacked on phones with the picture after the call to action,
 * so the first screen still ends in something to press.
 */
export function PageHero({
  kicker,
  title,
  accent,
  lead,
  children,
  visual,
  crumbs,
  badge,
}: {
  kicker: string;
  /** Shown next to the kicker, e.g. the Upcoming feature badge. */
  badge?: ReactNode;
  /** The H1. Keep it equal to the route manifest's h1 in English. */
  title: string;
  /** Optional tail of the H1, drawn in mint and arriving a beat later. */
  accent?: string;
  lead: ReactNode;
  children?: ReactNode;
  visual?: ReactNode;
  crumbs?: { label: string; to?: string }[];
}) {
  return (
    <section className="relative overflow-hidden px-4 pb-14 pt-28 sm:px-6 sm:pb-20 sm:pt-36">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[720px] bg-[radial-gradient(ellipse_55%_60%_at_60%_0%,hsl(var(--primary)/0.12),transparent_70%)]" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[820px] opacity-60 [background-image:radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_55%_45%_at_50%_30%,#000_30%,transparent_75%)]"
      />
      <div className={cn("relative z-10 mx-auto grid max-w-[1200px] grid-cols-1 gap-12", visual && "lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center lg:gap-14")}>
        {/* Painted straight away: the hero heading is the page's LCP and must not wait
            for the animation library to fade it in. */}
        <div className={cn(!visual && "mx-auto max-w-[860px] text-center")}>
          {crumbs && (
            <div>
              <nav aria-label="Breadcrumb" className={cn("mb-5 flex flex-wrap items-center gap-1.5 text-[12px] text-[hsl(var(--ec-muted))]", !visual && "justify-center")} style={{ fontFamily: MONO }}>
                {crumbs.map((c, i) => (
                  <span key={c.label} className="inline-flex items-center gap-1.5">
                    {i > 0 && <span aria-hidden>/</span>}
                    {c.to ? <Link to={c.to} className="hover:text-foreground">{c.label}</Link> : <span className="text-[hsl(var(--ec-text-2))]">{c.label}</span>}
                  </span>
                ))}
              </nav>
            </div>
          )}
          <div>
            <p className={cn("inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[0.12em] text-[hsl(var(--ec-mint))]", !visual && "justify-center")} style={{ fontFamily: MONO }}>
              <span aria-hidden className="h-px w-[18px] bg-[hsl(var(--ec-mint))]" />
              {kicker}
            </p>
            {badge && <span className={cn("ml-3 inline-flex align-middle", !visual && "mt-2 sm:mt-0")}>{badge}</span>}
          </div>
          <div>
            <h1 className="mt-5 text-[2.35rem] font-semibold leading-[1.05] tracking-[-0.045em] text-foreground sm:text-[3.4rem] lg:text-[60px]">
              {title}
              {accent && (
                <>
                  {" "}
                  <span className="text-[hsl(var(--ec-mint))]">{accent}</span>
                </>
              )}
            </h1>
          </div>
          <div>
            <div className={cn("mt-6 max-w-[620px] text-[17px] leading-relaxed text-[hsl(var(--ec-text-2))] sm:text-lg", !visual && "mx-auto")}>{lead}</div>
          </div>
          {children && <div className="mt-9">{children}</div>}
        </div>
        {visual && (
          <Reveal className="relative" from="right">
            {visual}
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Section chrome                                                      */
/* ------------------------------------------------------------------ */

export function Section({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={cn("px-4 py-16 sm:px-6 sm:py-24", className)}>
      <div className="mx-auto max-w-[1200px]">{children}</div>
    </section>
  );
}

/** Numbered like a document: the index and label on the left, the claim beside it. */
export function SectionHead({ idx, label, title, lead }: { idx: string; label: string; title: string; lead?: ReactNode }) {
  return (
    <Reveal className="relative grid grid-cols-1 gap-4 pt-6 md:grid-cols-[200px_minmax(0,1fr)] lg:grid-cols-[200px_minmax(0,1fr)_minmax(0,440px)] lg:items-end lg:gap-6">
      <DrawLine />
      <p className="self-start text-[12px] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
        <span className="text-[hsl(var(--ec-mint))]">{idx}</span>&nbsp;&nbsp;{label}
      </p>
      <h2 className="max-w-[700px] text-3xl font-semibold leading-[1.08] tracking-[-0.035em] text-foreground sm:text-[44px]">{title}</h2>
      {lead && <p className="text-[16px] leading-relaxed text-[hsl(var(--ec-text-2))] md:col-start-2 lg:col-start-auto">{lead}</p>}
    </Reveal>
  );
}

export function Panel({ children, className, lift = false }: { children: ReactNode; className?: string; lift?: boolean }) {
  return <div className={cn("h-full rounded-[18px] border border-border bg-card", lift && "ec-lift", className)}>{children}</div>;
}

/** A small mono label above a list or a block inside a panel. */
export function Label({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ec-muted))]", className)} style={{ fontFamily: MONO }}>
      {children}
    </p>
  );
}

export function CheckList({ items, className }: { items: ReactNode[]; className?: string }) {
  return (
    <ul className={cn("space-y-2.5", className)}>
      {items.map((g, i) => (
        <li key={i} className="flex items-start gap-2.5 text-[15px] leading-snug">
          <span className="mt-[3px] grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary/15 text-[hsl(var(--ec-mint))]"><Glyph name="check" size={10} /></span>
          <span>{g}</span>
        </li>
      ))}
    </ul>
  );
}

/** A finding the way the report writes it, for examples inside sections. */
export function FindingCard({ n, title, body, level, why, className }: { n: number; title: string; body?: string; level?: Level; why?: ReactNode; className?: string }) {
  const { tr } = useTr();
  const lv = level ? (level === "High" ? tr("Υψηλή", "High") : level === "Medium" ? tr("Μεσαία", "Medium") : tr("Χαμηλή", "Low")) : null;
  return (
    <div className={cn("rounded-xl border border-[hsl(var(--ec-line-strong))] bg-background/60 p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <Num n={n} />
          <p className="pt-0.5 text-[14.5px] font-semibold leading-snug">{title}</p>
        </div>
        {lv && (
          <span className={cn("shrink-0 rounded-full border px-2 py-[3px] text-[11px] font-medium", level === "High" ? "border-[hsl(var(--ec-mint)/0.5)] bg-primary/[0.08] text-[hsl(var(--ec-mint))]" : "border-white/[0.2] text-foreground/85")}>
            {lv}
          </span>
        )}
      </div>
      {body && <p className="mt-2.5 text-[13.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">{body}</p>}
      {why && <p className="mt-2.5 border-t border-border pt-2.5 text-[12.5px] leading-relaxed text-[hsl(var(--ec-muted))]">{why}</p>}
    </div>
  );
}

/** Before and after, stacked, with mono captions. */
export function BeforeAfter({ before, after, beforeLabel, afterLabel, className }: { before: ReactNode; after: ReactNode; beforeLabel?: string; afterLabel?: string; className?: string }) {
  const { tr } = useTr();
  return (
    <div className={cn("space-y-3", className)}>
      <figure>
        <figcaption className="mb-1.5 text-[11.5px] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{beforeLabel ?? tr("ΤΩΡΑ", "NOW")}</figcaption>
        {before}
      </figure>
      <figure>
        <figcaption className="mb-1.5 text-[11.5px] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>{afterLabel ?? tr("ΜΕΤΑ", "AFTER")}</figcaption>
        {after}
      </figure>
    </div>
  );
}

/** Figures that count up when they scroll in. */
export function StatStrip({ stats }: { stats: { value: number; format: (n: number) => string; label: string; note?: string }[] }) {
  return (
    <RevealGroup className="grid grid-cols-2 gap-px overflow-hidden rounded-[18px] border border-border bg-border md:grid-cols-4" stagger={0.08}>
      {stats.map((s) => (
        <Reveal key={s.label} className="bg-card px-5 py-6 sm:px-6">
          <p className="text-[34px] font-medium leading-none tracking-[-0.04em] text-[hsl(var(--ec-mint))] sm:text-[42px]" style={{ fontVariantNumeric: "tabular-nums" }}>
            <CountUp value={s.value} format={s.format} />
          </p>
          <p className="mt-2.5 text-[14px] leading-snug text-foreground">{s.label}</p>
          {s.note && <p className="mt-1 text-[12px] leading-snug text-[hsl(var(--ec-muted))]">{s.note}</p>}
        </Reveal>
      ))}
    </RevealGroup>
  );
}

/** Cards in a grid, each with a mono index, a title, a body and an optional picture. */
export function CardGrid({ items, cols = 3 }: { items: { title: string; body: ReactNode; visual?: ReactNode; kicker?: string }[]; cols?: 2 | 3 | 4 }) {
  return (
    <RevealGroup
      className={cn("mt-12 grid grid-cols-1 gap-3.5", cols === 2 && "md:grid-cols-2", cols === 3 && "md:grid-cols-2 lg:grid-cols-3", cols === 4 && "sm:grid-cols-2 lg:grid-cols-4")}
      stagger={0.07}
    >
      {items.map((it, i) => (
        <Reveal key={it.title}>
          <Panel lift className="flex flex-col overflow-hidden p-6">
            <p className="text-[12px] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>{it.kicker ?? `0${i + 1}`}</p>
            <h3 className="mt-3 text-[19px] font-semibold tracking-[-0.015em]">{it.title}</h3>
            <div className="mt-2 text-[14.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">{it.body}</div>
            {it.visual && <div className="mt-5 lg:mt-auto lg:pt-5">{it.visual}</div>}
          </Panel>
        </Reveal>
      ))}
    </RevealGroup>
  );
}

/** Numbered steps joined by a line that fills as the section scrolls past. */
export function Steps({ steps }: { steps: { title: string; body: ReactNode; visual?: ReactNode }[] }) {
  const cols = steps.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3";
  return (
    <div className="relative mt-12">
      <ScrollLine vertical className="bottom-6 left-[17px] top-4 w-px md:hidden" />
      <ScrollLine className={cn("left-4 right-4 top-[17px] hidden h-px", steps.length === 4 ? "lg:block" : "md:block")} />
      <RevealGroup as="ol" className={cn("relative grid grid-cols-1 gap-10 md:gap-6", cols)} stagger={0.12}>
        {steps.map((st, i) => (
          <Reveal as="li" key={st.title} className="relative pl-14 md:pl-0">
            <span className="absolute left-0 top-0 grid h-9 w-9 place-items-center rounded-full border border-[hsl(var(--ec-mint)/0.5)] bg-background text-[12px] text-[hsl(var(--ec-mint))] md:relative" style={{ fontFamily: MONO }}>
              <span aria-hidden className="ec-ping absolute inset-0 rounded-full border border-[hsl(var(--ec-mint)/0.5)]" style={{ animationDelay: `${i * 0.7}s` }} />
              0{i + 1}
            </span>
            <h3 className="pt-1.5 text-[20px] font-semibold tracking-[-0.015em] md:mt-5 md:pt-0">{st.title}</h3>
            <div className="mt-2 text-[15px] leading-relaxed text-[hsl(var(--ec-text-2))]">{st.body}</div>
            {st.visual && (
              <Panel lift className="mt-5 h-[190px] overflow-hidden rounded-[14px] bg-[radial-gradient(ellipse_70%_70%_at_50%_40%,hsl(var(--primary)/0.08),transparent_70%),hsl(var(--card))]">
                {st.visual}
              </Panel>
            )}
          </Reveal>
        ))}
      </RevealGroup>
    </div>
  );
}

/** Accordion of questions. The answers stay in the DOM, so crawlers read them. */
export function FaqList({ items, idPrefix = "faq" }: { items: { q: string; a: ReactNode }[]; idPrefix?: string }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <Reveal className="mt-10 grid grid-cols-1 md:grid-cols-[224px_minmax(0,1fr)]">
      <div />
      <div className="border-b border-border">
        {items.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={item.q} className="border-t border-border">
              <h3>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`${idPrefix}-${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left text-[17px] font-medium sm:text-lg"
                >
                  <span>{item.q}</span>
                  <span aria-hidden className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[hsl(var(--ec-line-strong))] text-[hsl(var(--ec-mint))] transition-transform duration-300", isOpen && "rotate-45")}>
                    <Glyph name="plus" size={12} />
                  </span>
                </button>
              </h3>
              <div id={`${idPrefix}-${i}`} hidden={!isOpen}>
                <div className="max-w-[760px] pb-6 text-[15px] leading-relaxed text-[hsl(var(--ec-text-2))]">{item.a}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Reveal>
  );
}

/** The closing panel every page ends on: one line, one form. */
export function CtaBand({ title, body, form = true, children }: { title?: string; body?: string; form?: boolean; children?: ReactNode }) {
  const { tr } = useTr();
  return (
    <Section className="pb-24 sm:pb-32">
      <Reveal>
        <div className="relative grid grid-cols-1 overflow-hidden rounded-[26px] border border-border bg-card lg:h-[440px] lg:grid-cols-[minmax(0,1fr)_440px]">
          <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(54,181,136,0.16),transparent_70%)]" />
          <div className="relative flex flex-col items-start px-7 py-12 sm:px-14 sm:py-16">
            <h2 className="max-w-[560px] text-[34px] font-semibold leading-[1.05] tracking-[-0.04em] sm:text-5xl">{title ?? tr("Δείτε τι χάνει η σελίδα σας", "See what your page is losing")}</h2>
            <p className="mt-4 max-w-[480px] text-[17px] leading-relaxed text-[hsl(var(--ec-text-2))]">
              {body ??
                tr(
                  "Μία δωρεάν ανάλυση, χωρίς κάρτα. Αν δεν σας πει κάτι που δεν ξέρατε, το ξέρετε σε πέντε λεπτά.",
                  "One free audit, no card. If it does not tell you something you did not know, you will know that in five minutes.",
                )}
            </p>
            {form && <AuditForm id="cta-band-url" className="mt-8 w-full max-w-[520px]" />}
            {children}
          </div>
          <div aria-hidden className="relative hidden bg-[radial-gradient(ellipse_70%_60%_at_60%_40%,hsl(var(--primary)/0.14),transparent_70%)] lg:block">
            <Parallax speed={30} className="absolute left-[60px] top-[50px]">
              <div className="-rotate-[4deg]">
                <Capture scale={0.5} markers={MOBILE_MARKERS.slice(0, 2)} radius={14} />
              </div>
            </Parallax>
            <Parallax speed={60} className="absolute left-[240px] top-[110px] w-[170px] rounded-[18px] border border-border bg-card/95 px-[18px] py-4">
              <ScoreBadge size={134} score={64} reach={81} />
              <p className="mt-1 text-[12px] text-[hsl(var(--ec-text-2))]">
                {tr("Φτάνει ", "Reachable ")}<span className="text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>81</span>
              </p>
            </Parallax>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/** A two or three column comparison, EyeCaptain first. */
export function CompareTable({ head, rows, highlight = 1 }: { head: string[]; rows: ReactNode[][]; highlight?: number }) {
  return (
    <Reveal className="mt-12 overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full min-w-[560px] text-left text-[14.5px]">
        <thead>
          <tr className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
            {head.map((h, i) => (
              <th key={i} scope="col" className={cn("px-5 py-4 font-normal", i === highlight && "text-[hsl(var(--ec-mint))]")}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-border align-top">
              {r.map((c, ci) =>
                ci === 0 ? (
                  <th key={ci} scope="row" className="px-5 py-4 font-normal text-[hsl(var(--ec-muted))]">{c}</th>
                ) : (
                  <td key={ci} className={cn("px-5 py-4", ci === highlight ? "font-medium text-foreground" : "text-[hsl(var(--ec-text-2))]")}>{c}</td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Reveal>
  );
}

/** Long-form copy in the site's type. The project has no typography plugin. */
export function Prose({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "text-[17px] leading-[1.8] text-[hsl(var(--ec-text-2))]",
        "[&_h2]:mb-5 [&_h2]:mt-14 [&_h2]:text-[28px] [&_h2]:font-semibold [&_h2]:leading-[1.12] [&_h2]:tracking-[-0.03em] [&_h2]:text-foreground sm:[&_h2]:text-[34px] [&_h2:first-child]:mt-0",
        "[&_h3]:mb-3 [&_h3]:mt-10 [&_h3]:text-[21px] [&_h3]:font-semibold [&_h3]:tracking-[-0.015em] [&_h3]:text-foreground",
        "[&_p]:mb-5",
        "[&_a]:font-medium [&_a]:text-[hsl(var(--ec-mint))] [&_a]:underline [&_a]:decoration-[hsl(var(--ec-mint)/0.35)] [&_a]:underline-offset-4 hover:[&_a]:decoration-[hsl(var(--ec-mint))]",
        "[&_strong]:font-semibold [&_strong]:text-foreground",
        "[&_ul]:my-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_ul>li::marker]:text-[hsl(var(--ec-mint))]",
        "[&_ol]:my-6 [&_ol]:list-decimal [&_ol]:space-y-3 [&_ol]:pl-6 [&_ol>li::marker]:text-[hsl(var(--ec-mint))]",
        "[&_blockquote]:my-8 [&_blockquote]:border-l-2 [&_blockquote]:border-[hsl(var(--ec-mint))] [&_blockquote]:pl-6 [&_blockquote]:text-foreground/80",
        "[&_table]:my-8 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:rounded-xl [&_table]:border [&_table]:border-border [&_table]:text-[14px] md:[&_table]:table",
        "[&_th]:bg-foreground/[0.03] [&_th]:p-3 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground",
        "[&_td]:border-t [&_td]:border-border [&_td]:p-3 [&_td]:align-top",
        "[&_code]:rounded [&_code]:bg-foreground/[0.06] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_code]:text-foreground",
        "[&_img]:my-8 [&_img]:rounded-2xl [&_img]:border [&_img]:border-border",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** The sample report's findings, shared by the heroes that show the KYMA page. */
export function kymaFindings(tr: Tr): { n: number; title: string; sub: string; level: Level }[] {
  return [
    { n: 1, title: tr("Το badge της έκπτωσης παίρνει την πρώτη ματιά", "The discount badge takes the first look"), sub: tr("Φαίνεται πριν από το προϊόν και την τιμή.", "Seen before the product and the price."), level: "High" },
    { n: 2, title: tr("Το Add to bag δύσκολα φαίνεται", "Add to bag is hard to see"), sub: tr("Τρίτο σε οπτικό βάρος στη σελίδα.", "Third in visual weight on the page."), level: "High" },
    { n: 3, title: tr("Αποστολή και επιστροφές κλειστά", "Delivery and returns folded away"), sub: tr("Οι δωρεάν επιστροφές είναι το πιο δυνατό επιχείρημα εδώ.", "Free returns is the best reassurance here."), level: "Medium" },
    { n: 4, title: tr("Καμία κριτική δίπλα στην τιμή", "No reviews next to the price"), sub: tr("Τίποτα δεν λέει ότι το αγόρασαν κι άλλοι.", "Nothing says that others bought it."), level: "Medium" },
    { n: 5, title: tr("Οδηγός μεγεθών μόνο στο desktop", "Size guide only on desktop"), sub: tr("Ο πιο συχνός λόγος επιστροφής για t-shirt.", "The most common return reason for tees."), level: "Low" },
  ];
}

export { Kicker, Reveal, RevealGroup };
